import { FC, Suspense, useMemo } from "react";

import { Pill, Label } from "../../components/common";
import { ArchwayDetails } from "../../components/forms/invoice/ArchwayDetails";
import { InvoiceHeaderToolbar } from "../../components/forms/invoice/InvoiceHeaderToolbar";
import { JobsList } from "../../components/forms/invoice/JobsList";
import { SideForm } from "../../components/forms/invoice/SideForm";
import { Logo } from "../../components/logo";
import { InvoiceSidebarBusiness } from "../../components/sidebar/invoice";
import {
  CURRENCY_MAP,
  DEFAULT_FIAT_CURRENCY,
  STATUS_CONFIG,
} from "../../constants";
import InvoiceLayout from "../../layouts/Invoice.layout";
import { StatusSet } from "../../types/GlobalTypes";
import {
  Currency,
  CurrencyType,
  InvoiceStatus,
  type Invoice,
} from "../../types/InvoiceTypes";
import { formatDate } from "../../utils/date";
import { InvoiceSummary } from "../../components/forms/invoice/InvoiceSummary";
import { InvoiceTotal } from "../../components/forms/invoice/InvoiceTotal";

const STATUS_PROPS = STATUS_CONFIG[StatusSet.INVOICE];

type Props = {
  data: Invoice;
};

export const InvoiceBusiness: FC<Props> = ({ data }) => {
  const {
    number,
    status,
    currency,
    issued_at,
    due_at,
    incoming_payment_details,
    note,
    show_transaction_data_on_public_view,
  } = data;

  const showBankDetails = ![
    InvoiceStatus.DRAFT,
    InvoiceStatus.CANCELLED,
  ].includes(status);

  const currencyDetails = CURRENCY_MAP[currency || DEFAULT_FIAT_CURRENCY];

  const paymentDetailsMessage = useMemo(() => {
    if (currencyDetails.type === CurrencyType.FIAT) {
      const sepaText =
        currencyDetails.value === Currency.EUR ? (
          <>
            {" "}
            through{" "}
            <span className="font-extrabold">SEPA/SEPA-Instant only</span>
          </>
        ) : undefined;

      return (
        <>
          Use these details to send{" "}
          <span className="font-extrabold uppercase">
            {currencyDetails.value}
          </span>
          {sepaText}. <span className="font-extrabold">IMPORTANT</span>: You
          must include the{" "}
          <span className="font-extrabold">REF # / MEMO / Comment</span> when
          creating the payment, or it may not be processed.
        </>
      );
    }

    return (
      <>
        Use these details to send{" "}
        <span className="font-extrabold uppercase">
          {currencyDetails.value}
        </span>
        . Make sure you pay to the right{" "}
        <span className="font-extrabold">ADDRESS</span>
      </>
    );
  }, [currencyDetails]);

  return (
    <Suspense fallback={Loader}>
      <InvoiceLayout sidebar={<InvoiceSidebarBusiness data={data} />}>
        <div className="mb-10">
          <header className="flex flex-row w-full items-center justify-between">
            <Logo />

            <InvoiceHeaderToolbar publicView={true} />
          </header>
        </div>

        <div className="flex flex-col gap-8 md:gap-12 mb-16 bg-white px-2 pt-4 md:px-8 md:pt-8 pb-16 rounded-2xl border border-gray-100">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="font-source-serif text-5xl">Invoice #{number}</div>

            <div className="mr-auto ml-0 md:mr-0 md:ml-auto">
              <Pill {...STATUS_PROPS[status]} />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <SideForm side="from" data={data} editable={false} />

            <SideForm side="to" data={data} editable={false} />
          </div>

          <div className="flex flex-row gap-x-6">
            <Label
              label="Issue date"
              labelClassName="mb-1 w-32 theme-caption uppercase"
            >
              <span className="theme-text">
                {issued_at && formatDate(issued_at)}
              </span>
            </Label>

            <Label
              label="Due date"
              labelClassName="mb-1 w-32 theme-caption uppercase"
            >
              <span className="theme-text">{due_at && formatDate(due_at)}</span>
            </Label>
          </div>

          <JobsList invoice={data} />

          {show_transaction_data_on_public_view ? (
            <InvoiceSummary className="mt-8" />
          ) : (
            <InvoiceTotal data={data} />
          )}

          <ArchwayDetails
            show={showBankDetails}
            message={paymentDetailsMessage}
            currency={currency}
            data={incoming_payment_details}
          />

          {note && (
            <Label
              label="Note"
              labelClassName="w-72 theme-caption uppercase mt-16"
            >
              <span className="theme-text break-all">{note}</span>
            </Label>
          )}
        </div>
      </InvoiceLayout>
    </Suspense>
  );
};

const Loader = <>Loading...</>;
